import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["min", "max", "step", "value", "disabled", "onChange", "onRawValueChange", "readOnly", "trackStart", "trackEnd", "onKeyDown", "onDragStart", "onDragEnd", "showLabels", "tabIndex", "minLabel", "size", "showTickMarks", "maxLabel", "restrictedAreaTooltip", "aria-label"],
  _excluded2 = ["value", "restricted", "width", "left"];
import devLogger from 'react-utils/devLogger';
import { useState } from 'react';
import { Controllable } from '../Controllable/Controllable';
import { callIfPossible } from '../../utils/functions/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { SliderTickMark } from './SliderTickMark';
import { SliderTrackSegment } from './SliderTrackSegment';
import { AbstractSlider } from '../AbstractSlider/AbstractSlider';
import { getTabIndex } from '../../utils/getTabIndex/getTabIndex';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { Flex } from '../../../components/Flex/Flex';
import { View } from '../../../components/View/View';
import { Text } from '../../../components/Text/Text';
import { createGlobalStyle } from 'styled-components';
import styled from 'foundations-theming/utils';
import { Tooltip } from '../Tooltip/Tooltip';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledGlobalCursor = createGlobalStyle(({
  $isDragging
}) => {
  return {
    body: {
      cursor: $isDragging ? 'grabbing' : undefined
    }
  };
});
const StyledSliderLabelContainer = styled.div.withConfig({
  displayName: "Slider__StyledSliderLabelContainer",
  componentId: "sc-2opz9r-0"
})(({
  $shorthandSize
}) => {
  return {
    position: 'relative',
    flexShrink: 0,
    userSelect: 'none',
    insetBlockStart: $shorthandSize === 'lg' ? '0px' : '-6px'
  };
});
const StyledSliderContainer = styled.div.withConfig({
  displayName: "Slider__StyledSliderContainer",
  componentId: "sc-2opz9r-1"
})(({
  'aria-disabled': ariaDisabled,
  'aria-readonly': ariaReadonly,
  $isDragging,
  $cssStyles,
  theme
}) => {
  const getCursorValue = () => {
    let cursor;
    if (ariaDisabled) {
      cursor = 'not-allowed';
    } else if (ariaReadonly) {
      cursor = 'initial';
    } else if ($isDragging) {
      cursor = 'grabbing';
    } else {
      cursor = 'pointer';
    }
    return cursor;
  };
  return Object.assign({
    display: 'flex',
    flexDirection: 'row',
    cursor: getCursorValue(),
    blockSize: theme.space[500]
  }, $cssStyles);
});
const StyledSliderControl = styled.div.withConfig({
  displayName: "Slider__StyledSliderControl",
  componentId: "sc-2opz9r-2"
})(({
  $isDragging,
  $shorthandSize,
  $isDisabled,
  $isReadOnly,
  $insetInlineStart,
  theme
}) => {
  const getCursorValue = () => {
    let cursor;
    if ($isDisabled) {
      cursor = 'not-allowed';
    } else if ($isReadOnly) {
      cursor = 'initial';
    } else if ($isDragging) {
      cursor = 'grabbing';
    } else {
      cursor = 'grab';
    }
    return cursor;
  };
  return {
    cursor: getCursorValue(),
    backgroundColor: $isReadOnly ? theme.color['fill-tertiary-default'] : theme.color['fill-surface-default'],
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: $isReadOnly ? theme.color['fill-tertiary-default'] : theme.color['text-primary-subtle'],
    boxShadow: theme.boxShadow['100'],
    blockSize: theme.space[500],
    position: 'relative',
    insetBlockStart: $shorthandSize === 'lg' ? '0px' : '-4px',
    transform: `translateX(-${parseInt(theme.space[500], 10) / 2}px)`,
    inlineSize: theme.space[500],
    insetInlineStart: $insetInlineStart
  };
});
const getFirstDefinedValue = (...values) => values.find(val => val != null);
const getAdjustedValue = (min, max, step, rawValue) => {
  if (rawValue < min) return min;
  if (rawValue > max) return max;
  if (Math.abs(rawValue % step) !== 0) {
    return Math.round(rawValue / step) * step;
  }
  return rawValue;
};
const getControlPositionFromValue = (min, max, adjustedValue) => {
  if (max - min === 0) {
    return 0;
  }
  return (adjustedValue - min) / (max - min) * 100;
};
export const InternalSlider = _ref => {
  let {
      min = 0,
      max = 100,
      step = 1,
      value = null,
      disabled = false,
      onChange,
      onRawValueChange,
      readOnly = false,
      trackStart,
      trackEnd,
      onKeyDown,
      onDragStart,
      onDragEnd,
      showLabels,
      tabIndex,
      minLabel,
      size = 'default',
      showTickMarks,
      maxLabel,
      restrictedAreaTooltip: restrictedAreaTooltipProp,
      'aria-label': ariaLabel
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [isDragging, setIsDragging] = useState(false);
  const handleChange = rawValue => {
    if (disabled || readOnly) {
      return;
    }
    const adjustedRawValue = getAdjustedValue(
    // @ts-expect-error Argument of type 'number | undefined' is not assignable to parameter of type 'number'
    trackStart, trackEnd, step, rawValue);
    callIfPossible(onRawValueChange, syntheticEvent(adjustedRawValue));
    const newValue = getAdjustedValue(min, max, step, rawValue);
    if (value !== newValue) {
      onChange === null || onChange === void 0 || onChange(syntheticEvent(newValue));
    }
  };
  const handleKeyDown = evt => {
    let nextValue = value || 0;
    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowDown') {
      evt.preventDefault();

      // @ts-expect-error 'value' is possibly 'null'
      nextValue = value - step;
    } else if (evt.key === 'ArrowRight' || evt.key === 'ArrowUp') {
      evt.preventDefault();

      // @ts-expect-error 'value' is possibly 'null'
      nextValue = value + step;
    }
    handleChange(nextValue);
    callIfPossible(onKeyDown, evt);
  };
  const getControlValueFromPercentX = percentX => {
    const minVal = getFirstDefinedValue(trackStart, min, 0);
    const maxVal = getFirstDefinedValue(trackEnd, max, 0);
    return percentX * (maxVal - minVal) + minVal;
  };
  const handleDrag = evt => {
    const {
      percentX
    } = evt.target.value;
    handleChange(getControlValueFromPercentX(percentX));
  };
  const handleDragStart = () => {
    setIsDragging(true);
    callIfPossible(onDragStart);
  };
  const handleDragEnd = () => {
    setIsDragging(false);
    callIfPossible(onDragEnd);
  };
  const handleMinLabelClick = () => {
    if (!disabled && !readOnly) handleChange(min);
  };
  const handleMaxLabelClick = () => {
    if (!disabled && !readOnly) handleChange(max);
  };
  const renderLabel = (shorthandSize, val, label, name) => {
    // const use = shorthandSize === 'lg' ? null : 'help';

    return /*#__PURE__*/_jsx(StyledSliderLabelContainer, {
      $shorthandSize: shorthandSize,
      onClick: name === 'min' ? handleMinLabelClick : handleMaxLabelClick,
      children: /*#__PURE__*/_jsx(Text, {
        as: shorthandSize === 'lg' ? 'h5' : 'small',
        paddingInlineStart: name === 'max' ? '400' : undefined,
        paddingInlineEnd: name === 'min' ? '400' : undefined
        // use={use} // Not a valid HTML attribute
        ,
        children: label || val
      })
    });
  };
  const renderTrackSegment = (restrictedAreaTooltip, shorthandSize, _ref2) => {
    let {
        value: trackSegmentVal,
        restricted,
        width,
        left
      } = _ref2,
      trackSegmentProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    // @ts-expect-error 'width' is possibly 'undefined'
    if (width <= 0) {
      return null;
    }
    const track = /*#__PURE__*/_jsx(SliderTrackSegment, Object.assign({}, trackSegmentProps, {
      left: left,
      width: width,
      size: shorthandSize === 'lg' ? shorthandSize : 'md',
      restricted: restricted,
      disabled: disabled,
      readOnly: readOnly,
      value: trackSegmentVal
    }));
    if (restrictedAreaTooltip) {
      const tooltipTitle = typeof restrictedAreaTooltip === 'function' ?
      // @ts-expect-error - 'value' is possibly 'null'
      restrictedAreaTooltip(trackSegmentVal > 0 ? 'upper' : 'lower') : restrictedAreaTooltip;
      return /*#__PURE__*/_jsx(Tooltip, {
        title: tooltipTitle,
        placement: "bottom",
        children: track
      });
    }
    return track;
  };
  const renderTickMarks = (renderedMax, renderedMin, shorthandSize) => {
    const numberOfTickMarks = (renderedMax - renderedMin) / step;
    const tickMarks = [];
    for (let i = 0; i <= numberOfTickMarks; i++) {
      tickMarks.push( /*#__PURE__*/_jsx(SliderTickMark, {
        currentSliderValue: value,
        value: i * step + renderedMin,
        min: min,
        shorthandSize: shorthandSize,
        readOnly: readOnly,
        disabled: disabled
      }, i));
    }
    return tickMarks;
  };
  if (process.env.NODE_ENV !== 'production') {
    if (Math.abs(min % step) !== 0 || Math.abs(max % step) !== 0) {
      devLogger.warn({
        message: 'Slider: Min and max value should be a multiple of the step',
        key: 'Slider: Min or max not a multiple of the step'
      });
    }
    if (trackStart != null && trackStart > min) {
      devLogger.warn({
        message: 'Slider: trackStart value should be less than the min',
        key: 'Slider: trackStart greater than min'
      });
    }
    if (trackEnd != null && trackEnd < max) {
      devLogger.warn({
        message: 'Slider: trackEnd value should be greater than the max',
        key: 'Slider: trackEnd less than max'
      });
    }
  }
  const shorthandSize = toShorthandSize(size);
  // @ts-expect-error Argument of type 'number | null' is not assignable to parameter of type 'number'
  const adjustedValue = getAdjustedValue(min, max, step, value);
  const renderedMin = getFirstDefinedValue(trackStart, min, 0);
  const renderedMax = getFirstDefinedValue(trackEnd, max, 0);
  const trackLength = renderedMax - renderedMin;
  const controlPosition = getControlPositionFromValue(renderedMin, renderedMax, adjustedValue);
  let percentRestrictedLower = 0;
  let percentRestrictedUpper = 0;
  let percentFilled = 0;
  let percentAvailable = 0;
  if (trackLength > 0) {
    percentRestrictedLower = (min - renderedMin) / trackLength * 100;
    percentRestrictedUpper = (renderedMax - max) / trackLength * 100;
    percentFilled = controlPosition - percentRestrictedLower;
    percentAvailable = 100 - percentRestrictedUpper - controlPosition;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StyledGlobalCursor, {
      $isDragging: isDragging
    }), /*#__PURE__*/_jsxs(StyledSliderContainer, Object.assign({}, domProps, {
      $cssStyles: cssStyles,
      "aria-disabled": disabled,
      "aria-readonly": readOnly,
      "aria-valuemax": max,
      "aria-valuemin": min,
      "aria-valuenow": value || 0,
      "aria-valuetext": `${value || 0}`,
      "aria-label": ariaLabel,
      onKeyDown: handleKeyDown,
      role: "slider",
      tabIndex: getTabIndex(disabled, tabIndex),
      $isDragging: isDragging,
      children: [showLabels && renderLabel(shorthandSize, renderedMin, minLabel, 'min'), /*#__PURE__*/_jsx(AbstractSlider, {
        onDrag: handleDrag,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        flexGrow: 1,
        children: /*#__PURE__*/_jsxs(View, {
          position: "relative",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexDirection: "row",
            justifyContent: "space-between",
            position: "absolute",
            inlineSize: "100%",
            children: showTickMarks && renderTickMarks(renderedMax, renderedMin, shorthandSize)
          }), renderTrackSegment(restrictedAreaTooltipProp, shorthandSize, {
            width: percentRestrictedLower,
            restricted: true,
            value: -1
          }), renderTrackSegment(null, shorthandSize, {
            left: percentRestrictedLower,
            width: percentFilled,
            value: 0
          }), renderTrackSegment(null, shorthandSize, {
            left: controlPosition,
            width: percentAvailable,
            value: 1
          }), renderTrackSegment(restrictedAreaTooltipProp, shorthandSize, {
            left: controlPosition + percentAvailable,
            width: percentRestrictedUpper,
            restricted: true,
            value: 1
          }), /*#__PURE__*/_jsx(StyledSliderControl, {
            $isDragging: isDragging,
            $shorthandSize: shorthandSize,
            $isDisabled: disabled,
            $isReadOnly: readOnly,
            $insetInlineStart: `${controlPosition}%`
          })]
        })
      }), showLabels && renderLabel(shorthandSize, renderedMax, maxLabel, 'max')]
    }))]
  });
};
export const Slider = Controllable(InternalSlider);